import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Check, ExternalLink, Sparkles, Zap, Crown, Clock, AlertTriangle, Repeat, Settings2 } from "lucide-react";

const PLAN_ICONS = {
  basico: Sparkles,
  pro: Zap,
  enterprise: Crown,
};

const fmtBRL = (n) => `R$ ${Number(n).toFixed(2).replace(".", ",")}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

const daysLeft = (iso) => {
  if (!iso) return null;
  const diff = new Date(iso) - new Date();
  return Math.ceil(diff / 86_400_000);
};

export const SubscriptionTab = () => {
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutId, setCheckoutId] = useState(null);
  const [autoRenew, setAutoRenew] = useState(false);
  const [billingCycle, setBillingCycle] = useState("annual");
  const [annualDiscountPct, setAnnualDiscountPct] = useState(30);
  const [portalLoading, setPortalLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, statusRes] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/status"),
      ]);
      setPlans(plansRes.data.plans);
      if (typeof plansRes.data.annual_discount_pct === "number") {
        setAnnualDiscountPct(plansRes.data.annual_discount_pct);
      }
      setStatus(statusRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const startCheckout = async (planId) => {
    setCheckoutId(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan: planId,
        origin_url: window.location.origin,
        subscription: autoRenew,
        interval: billingCycle,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setCheckoutId(null);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: `${window.location.origin}/admin`,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setPortalLoading(false);
    }
  };

  if (loading) return <div className="text-foreground/50 text-sm">Carregando assinatura…</div>;

  const sub = status?.subscription;
  const isTrial = sub?.status === "trial";
  const active = status?.active;
  const trialDaysLeft = isTrial ? daysLeft(sub?.trial_ends_at) : null;
  const expired = sub && !active;

  return (
    <div className="space-y-8">
      {/* Status banner */}
      <div
        className={`border p-6 md:p-8 ${
          expired ? "border-destructive bg-destructive/5" : isTrial ? "border-foreground" : "border-border"
        }`}
        data-testid="billing-status-card"
      >
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-foreground/60 mb-3">
              {expired ? <AlertTriangle className="w-4 h-4 text-destructive" /> : <Clock className="w-4 h-4" />}
              <span>{expired ? "Assinatura Expirada" : "Plano Atual"}</span>
            </div>
            <div className="font-heading text-3xl md:text-4xl font-black tracking-tighter mb-2" data-testid="billing-plan-label">
              {status?.plan_label || "—"}
            </div>
            <div className="text-sm text-foreground/70">{status?.limits?.label}</div>
          </div>
          <div className="text-right">
            {isTrial && trialDaysLeft !== null && (
              <>
                <div className="font-heading text-5xl font-black tracking-tighter" data-testid="billing-trial-days">
                  {Math.max(0, trialDaysLeft)}
                </div>
                <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mt-1">
                  {trialDaysLeft === 1 ? "dia restante" : "dias restantes"}
                </div>
              </>
            )}
            {!isTrial && !expired && (
              <>
                <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-1">Próxima cobrança</div>
                <div className="font-mono text-sm" data-testid="billing-paid-until">
                  {fmtDate(sub?.paid_until)}
                </div>
              </>
            )}
            {expired && (
              <div className="text-sm uppercase tracking-[0.2em] text-destructive font-semibold">
                Modo somente leitura
              </div>
            )}
          </div>
        </div>
        {expired && (
          <p className="text-sm text-foreground/70 mt-4 max-w-2xl">
            Chamadas de senha estão bloqueadas até que você renove a assinatura. Configurações
            e visualização da TV continuam disponíveis.
          </p>
        )}
      </div>

      {/* Portal + auto-renew controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-border p-5 flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-foreground/60 mb-2">
              <Repeat className="w-3.5 h-3.5" /> Auto-renovação Stripe
            </div>
            <div className="text-sm font-medium mb-1">
              {autoRenew ? "Cobrança recorrente mensal" : "Pagamento avulso (+30 dias por clique)"}
            </div>
            <p className="text-xs text-foreground/60">
              Quando ativado, o Stripe cobra automaticamente seu cartão a cada mês. Caso seu plano
              ainda não tenha o Price ID configurado, o pagamento vira avulso.
            </p>
          </div>
          <Switch
            checked={autoRenew}
            onCheckedChange={setAutoRenew}
            data-testid="billing-autorenew-toggle"
          />
        </div>

        <div className="border border-border p-5 flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-foreground/60 mb-2">
              <Settings2 className="w-3.5 h-3.5" /> Portal Stripe (self-service)
            </div>
            <div className="text-sm font-medium mb-1">Gerenciar cartão, plano, faturas</div>
            <p className="text-xs text-foreground/60">
              Cancele a assinatura, troque de plano, baixe faturas ou atualize o cartão diretamente
              no portal seguro do Stripe.
              {!sub?.stripe_customer_id && (
                <span className="block mt-2 text-foreground/40">
                  Disponível após o primeiro pagamento via Stripe.
                </span>
              )}
            </p>
          </div>
          <Button
            onClick={openPortal}
            disabled={!sub?.stripe_customer_id || portalLoading}
            variant="outline"
            className="rounded-none"
            data-testid="billing-portal-btn"
          >
            {portalLoading ? "Abrindo…" : "Abrir Portal"}
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Plan cards */}
      <div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="text-xs uppercase tracking-[0.25em] text-foreground/60">Assinatura</div>
          <div className="inline-flex border border-border" data-testid="billing-cycle-toggle">
            <button
              type="button"
              onClick={() => setBillingCycle("monthly")}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-semibold transition-all ${
                billingCycle === "monthly" ? "bg-foreground text-background" : "bg-background text-foreground hover:bg-foreground/5"
              }`}
              data-testid="billing-cycle-monthly-btn"
            >
              Mensal
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle("annual")}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-semibold transition-all border-l border-border ${
                billingCycle === "annual" ? "bg-foreground text-background" : "bg-background text-foreground hover:bg-foreground/5"
              }`}
              data-testid="billing-cycle-annual-btn"
            >
              Anual · {annualDiscountPct}% off
            </button>
          </div>
        </div>
        <div className={`grid grid-cols-1 gap-4 ${plans.length > 1 ? "md:grid-cols-3" : "md:grid-cols-1 max-w-2xl"}`}>
          {plans.map((p) => {
            const Icon = PLAN_ICONS[p.id] || Sparkles;
            const isCurrent = sub?.plan === p.id && active;
            const monthly = p.cycles?.monthly?.amount ?? p.price;
            const annualTotal = p.cycles?.annual?.amount ?? monthly * 12 * 0.7;
            const annualEffective = p.cycles?.annual?.effective_monthly ?? annualTotal / 12;
            const savings = p.cycles?.annual?.savings ?? monthly * 12 - annualTotal;
            const showAnnual = billingCycle === "annual";
            const displayPricePerMonth = showAnnual ? annualEffective : monthly;
            return (
              <Card
                key={p.id}
                className={`rounded-none shadow-none p-6 md:p-8 flex flex-col justify-between min-h-[440px] border ${
                  isCurrent ? "border-foreground bg-foreground text-background" : "border-border"
                }`}
                data-testid={`billing-plan-card-${p.id}`}
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <Icon className="w-7 h-7" strokeWidth={1.5} />
                    {isCurrent && (
                      <span className="text-[10px] uppercase tracking-[0.25em] border border-current px-2 py-1">
                        Atual
                      </span>
                    )}
                  </div>
                  <div className="text-xs uppercase tracking-[0.25em] opacity-60 mb-2">
                    {p.label} · {showAnnual ? "Anual" : "Mensal"}
                  </div>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="font-heading text-5xl font-black tracking-tighter" data-testid={`billing-price-${p.id}`}>
                      {fmtBRL(displayPricePerMonth)}
                    </span>
                    <span className="text-xs uppercase tracking-[0.2em] opacity-60">/mês</span>
                  </div>
                  {showAnnual ? (
                    <div className="text-xs opacity-70 mb-6" data-testid={`billing-annual-detail-${p.id}`}>
                      Cobrado {fmtBRL(annualTotal)}/ano · Economia de <strong>{fmtBRL(savings)}</strong>
                    </div>
                  ) : (
                    <div className="text-xs opacity-70 mb-6">
                      Cobrança mensal · Cancele quando quiser
                    </div>
                  )}
                  <ul className="space-y-3 text-sm">
                    <li className="flex gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        {p.limits.max_operators === -1
                          ? "Operadores ilimitados"
                          : `${p.limits.max_operators} operadores`}
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        {p.limits.max_desks === -1
                          ? "Guichês ilimitados"
                          : `${p.limits.max_desks} guichês`}
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        Mídia: {p.limits.media_modes.filter((m) => m !== "none").join(", ")}
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Histórico + customização visual completa</span>
                    </li>
                    {p.id === "enterprise" && (
                      <li className="flex gap-2">
                        <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>Suporte prioritário</span>
                      </li>
                    )}
                  </ul>
                </div>
                <Button
                  onClick={() => startCheckout(p.id)}
                  disabled={checkoutId === p.id}
                  className={`mt-8 rounded-none h-12 uppercase tracking-[0.2em] text-sm ${
                    isCurrent ? "bg-background text-foreground hover:bg-background/90" : ""
                  }`}
                  data-testid={`billing-checkout-${p.id}`}
                >
                  {checkoutId === p.id ? "Abrindo Stripe…" : isCurrent ? "Renovar agora" : "Assinar"}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </Card>
            );
          })}
        </div>
        <p className="text-xs text-foreground/50 mt-4">
          Pagamento processado via Stripe (cartão de crédito). Cada pagamento adiciona 30 dias à sua
          assinatura. Você pode trocar de plano a qualquer momento renovando com outro valor.
        </p>
      </div>
    </div>
  );
};
